import { countDecimals, fnAuto } from '../../../core/formatter/formatNumber';
import { DatenauswertungKennzahlenRenderer } from '../DatenauswertungKennzahlenRenderer';
import DialogWrapper2 from '../../../core/dialog/dialog_wrapper_v2';
import { IDs } from '../../../helpers/IDs';
import { i18n } from '../../../i18n/i18n';
import { Kennzahl } from '../../../main/functions/strukturiert/Kennzahl';
import { KennzahlFaktor } from '../../../main/functions/strukturiert/KennzahlFaktor';
import * as React from 'react';
import { WHelpIcon } from '../../../core/elements/WHelpIcon';

interface KennzahlenInfoProps extends React.ClassAttributes<any> {
    data: any;
    'data-text': string;
    einheit?: string;
    id: string;
    jahr: number;
    keyValue: string;
    standort: number | string;
    style?: React.CSSProperties;
}

interface KennzahlenInfoState {
    mouseOverInfo: number | string;
    showDialog: boolean;
}

export class KennzahlenInfo extends React.Component<KennzahlenInfoProps, KennzahlenInfoState> {

    constructor(props: any) {
        super(props);
        this.state = {
            mouseOverInfo: undefined,
            showDialog: false
        };
    }

    private data: any;
    private decimalPlaces: number;
    private formatFloat: any = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 17, useGrouping: true });
    private unit: string;
    private values: any[] = [];

    componentDidMount(): void {
        this.init();
    }

    getInput(data: any): any {
        const factors: number[] = [];
        const item: any = (data.items || [])[0] || data || {};
        const faktoren: any[] = item.faktoren || [];
        const isHochrechnung: boolean = faktoren.some((factor: any) => factor.type === KennzahlFaktor.TYPE_HOCHRECHNUNG);
        const isUmrechnung: boolean = faktoren.some((factor: any) => factor.type === KennzahlFaktor.TYPE_UMRECHNUNG);
        const factorProjection: number = (faktoren.find((factor: any) => factor.type === KennzahlFaktor.TYPE_HOCHRECHNUNG) || {}).value;
        const unit: string = !this.props.einheit && [Kennzahl.TYPE_BERECHNUNG, Kennzahl.TYPE_EINGABE].indexOf(data.type) === -1
            ? item.einheit || null
            : item.einheitOrig || item.einheit || null;
        const value: number = (!isHochrechnung && !isUmrechnung && this.props.einheit && data.field !== 'co2')
            || ((!this.props.einheit || this.unit !== 'kwh') && [Kennzahl.TYPE_BERECHNUNG, Kennzahl.TYPE_EINGABE].indexOf(data.type) === -1)
            ? item.value || null
            : item.valueOrig || item.value || null;

        if (
            (faktoren.length || typeof ((data.faktoren || [])[0] || {}).value === 'number' || !this.props.einheit)
            && data.field === 'co2' && typeof ((data.faktoren || [])[0] || {}).value === 'number'
        ) {
            factors.push(data.faktoren[0].value);

            faktoren.forEach((item2: any) => {
                if (typeof item2.value === 'number') {
                    factors.push(item2.value);
                }
            });
        } else if (typeof factorProjection === 'number' && data.field !== 'co2') {
            factors.push(factorProjection);
        }

        return { factorProjection, factors, unit: transformUnit(unit), value };
    }

    getInputCalc(data: any, callback?: (max: number) => void): string {
        const { factors, unit, value } = this.getInput(data);
        const faktoren = data.faktoren || [];

        if (typeof callback === 'function') {
            callback(Math.max(countDecimals(+value), ...factors.map((factor: number) => countDecimals(factor))));
        }

        if (!factors.length) {
            return null;
        }

        return [
            `${fnAuto(value, 7)} ${unit}`,
            // ...factors.map((factor: number) => this.formatFloat.format(factor))
            ...factors.map((factor: number, i: number) => `${fnAuto(factor, 7)}${faktoren[i] && faktoren[i].type === 'type_co2' ? ' tCO₂Äq/kWh' : ''}`)
        ].join(' × ');
    }

    getMaxValue(): number {
        return this.values.reduce((max: number, item: any) => {
            const factorProjection: number = ((item.faktoren || [])
                .find((factor: any) => factor.type === KennzahlFaktor.TYPE_HOCHRECHNUNG) || {}).value;
            const faktoren: any[] = item.faktoren || [];
            const isHochrechnung: boolean = faktoren.some((factor: any) => factor.type === KennzahlFaktor.TYPE_HOCHRECHNUNG);
            const isUmrechnung: boolean = faktoren.some((factor: any) => factor.type === KennzahlFaktor.TYPE_UMRECHNUNG);
            const value: number = (isHochrechnung && this.props.einheit && this.data.field !== 'co2' && !isUmrechnung)
                || (!this.props.einheit && [Kennzahl.TYPE_BERECHNUNG, Kennzahl.TYPE_EINGABE].indexOf(this.data.type) === -1)
                ? +item.value || 0
                : (factorProjection ? +item.valueOrig * factorProjection : +item.valueOrig) || +item.value || 0;
            return value > max ? value : max;
        }, null);
    }

    getSumOfValues(): number {
        let decimalPlaces: number = 0;
        const result: number = this.values.reduce((sum: number, item: any) => {
            const factorProjection: number = ((item.faktoren || [])
                .find((factor: any) => factor.type === KennzahlFaktor.TYPE_HOCHRECHNUNG) || {}).value;
            const faktoren: any[] = item.faktoren || [];
            const isHochrechnung: boolean = faktoren.some((factor: any) => factor.type === KennzahlFaktor.TYPE_HOCHRECHNUNG);
            const isUmrechnung: boolean = faktoren.some((factor: any) => factor.type === KennzahlFaktor.TYPE_UMRECHNUNG);
            const value: number = (isHochrechnung && this.props.einheit && this.data.field !== 'co2' && !isUmrechnung)
                || (!this.props.einheit && [Kennzahl.TYPE_BERECHNUNG, Kennzahl.TYPE_EINGABE].indexOf(this.data.type) === -1)
                ? +item.value || 0
                : (factorProjection ? +item.valueOrig * factorProjection : +item.valueOrig) || +item.value || 0;
            const curDecimalPlaces: number = countDecimals(+value);
            decimalPlaces = curDecimalPlaces > decimalPlaces ? curDecimalPlaces : decimalPlaces;
            return sum + value;
        }, 0);

        return this.transformValue(result, decimalPlaces);
    }

    init(): void {
        this.unit = (this.props.einheit || '').toString().toLowerCase() || null;
        const kennziffern: any = (this.props.data || {})[IDs.KENNZIFFERN_STRUKTURIERT] || {};
        const data: any = (((kennziffern[this.props.jahr]) || {})[this.props.standort] || {})[this.props.keyValue] || {};
        this.data = data;
        this.values = data.items || [];

        if (this.props.einheit) {
            this.values = this.values.filter((item: any) =>
                (((item.items || [])[0] || {}).einheitOrig || ((item.items || [])[0] || {}).einheit || '').toString().toLowerCase() === this.unit
                || (item.einheitOrig || item.einheit || '').toString().toLowerCase() === this.unit
            );
        }
        // console.info('DATA', this.values, this.data, ((kennziffern[this.props.jahr]) || {})[this.props.standort], kennziffern);
    }

    render(): JSX.Element {
        const hasValues: boolean = !!this.values.length;
        const id: string = this.props.id || null;

        return <>
            <span
                style={hasValues ? { cursor: 'pointer' } : {}}
                onClick={() => hasValues ? this.setState({ showDialog: true }) : null}
                onMouseEnter={() => this.setState({ mouseOverInfo: id })}
                onMouseLeave={() => this.setState({ mouseOverInfo: undefined })}
            >
                {this.props['data-text']}
            </span>
            <div
                ref={((ref: any) => this.setStyle(ref))}
                style={{ width: 320, maxWidth: 320, position: 'relative', ...(this.props.style || {}) }}
            >
                {this.state.mouseOverInfo === id && hasValues &&
                    this.renderInfo()
                }
            </div>
            {this.state.showDialog &&
                <DialogWrapper2
                    title={i18n('Kennzahlenberechnung', 'sys')}
                    width={-1}
                    onAfterClose={() => this.setState({ showDialog: false })}
                >
                    <WHelpIcon
                        helpKey='info_kennzahlenberechnung'
                        data={this.props.data}
                        styleDivProps={{ margin: '12px 12px -30px', display: 'inline-flex', justifyContent: 'flex-end' }}
                        styleProps={{ margin: 0 }}
                        label={`${i18n('Info', 'sys')} "${i18n('Kennzahlenberechnung', 'sys')}"`}
                        type='theme_info'
                        style='theme_small'
                        customElement='Info'
                        editBar
                        useTheme
                    />
                    <div style={{ minWidth: 800, maxWidth: 'calc(100vw - 40px)', boxSizing: 'border-box' }}>
                        <DatenauswertungKennzahlenRenderer
                            data={this.props.data}
                            kennzahlen={[this.data]}
                        />
                    </div>
                </DialogWrapper2>
            }
        </>;
    }

    renderAggregateOfValues(): JSX.Element {
        if (!this.values.length) {
            return;
        }
        const unit: string = !this.props.einheit && this.data.field !== 'co2'
            ? this.values[0].einheit || ''
            : this.values[0].einheitOrig || this.data.einheit || '';
        let element: JSX.Element = null;

        if (this.data.type === Kennzahl.TYPE_MAX) {
            element = <><b>Wert (Maximum):</b> {fnAuto(this.getMaxValue(), 7)} {transformUnit(unit)}</>;
        } else {
            const sum: number = this.transformValue(this.getSumOfValues(), this.decimalPlaces);
            // element = <><b>Wert (Summe):</b> {this.formatFloat.format(sum)} {transformUnit(unit)}</>;
            const formel = this.values.length > 1 ?
                this.values.map((v: any) => {
                    let value = v.value;
                    let einheit = v.einheit;
                    if (v.bereich !== Kennzahl.BEREICH_KRAFTSTOFFE) {
                        value = v.einheitOrig && transformUnit(v.einheitOrig) !== transformUnit(v.einheit) && v.valueOrig ? v.valueOrig : v.value;
                        einheit = v.einheitOrig || v.einheit;
                    }
                    return `${fnAuto(value || 0, 7)}${einheit ? ` ${transformUnit(einheit)}` : ''}`;
                }).join(' + ')
                : null;
            element = <><b>{i18n('Wert', 'sys')} ({i18n('Summe', 'sys')}):</b> {fnAuto(sum, 7)} {transformUnit(unit)}{formel ? ` = ${formel}` : ''}</>;
        }

        return (
            <div style={{ /* whiteSpace: 'nowrap' */ }}>
                {element}
            </div>
        );
    }

    renderInfo(): JSX.Element {
        return (
            <div
                style={{
                    fontSize: 12, fontStyle: 'normal', fontWeight: 'normal', color: 'black', textAlign: 'left', backgroundColor: '#e5e4e4',
                    maxWidth: 320, padding: 5, border: '1px solid black', position: 'absolute', zIndex: 1, overflow: 'hidden',
                    hyphens: 'auto'
                }}
            >
                {/*einheit: {this.unit}<br />
                id: {this.props.id}<br />
                jahr: {this.props.jahr}<br />
                key: {this.props.keyValue}<br />
                standort: {this.props.standort}<br />*/}
                <DatenauswertungKennzahlenRenderer
                    data={this.props.data}
                    kennzahlen={[this.data]}
                    isTooltip
                />
                {/*this.renderValues()*/}
                {/*this.renderAggregateOfValues()*/}
            </div>
        );
    }

    renderValue(data: any): JSX.Element {
        const { factorProjection, unit, value } = this.getInput(data);
        let decimalPlaces: number = 0;
        const calc: string = this.getInputCalc(data, (max: number) => decimalPlaces = max);
        const isCalc: boolean = (!this.props.einheit && [Kennzahl.TYPE_BERECHNUNG, Kennzahl.TYPE_EINGABE].indexOf(data.type) !== -1)
            || this.data.field === 'co2';
        const curValue: number = isCalc
            ? this.transformValue(data.value, decimalPlaces)
            : this.transformValue(factorProjection ? value * factorProjection : value, decimalPlaces);
        const curUnit: number = isCalc ? transformUnit(data.einheit) : unit;
        this.decimalPlaces = decimalPlaces > this.decimalPlaces ? decimalPlaces : this.decimalPlaces;

        return <>
            {fnAuto(curValue, 7)}{data.einheit ? ` ${curUnit}` : ''}
            {calc ? <>{` = ${calc}`}</> : ''}
        </>;
    }

    renderValues(): JSX.Element {
        if (!this.values.length) {
            return;
        }
        this.decimalPlaces = 0;

        return <>
            <b>{i18n(this.values.length > 1 ? 'Werte' : 'Wert', 'sys')}:</b>
            <ul style={{ margin: 0, paddingLeft: 16 }}>
                {this.values.map((item: any, index: number) =>
                    <li key={`value-${index}`}>
                        {this.renderValue(item)}
                    </li>
                )}
            </ul>
        </>;
    }

    setStyle(ref: any): void {
        if (!ref || !ref.children.length) {
            return;
        }
        ref.style.marginLeft = '100%';
        const offsetLeft: number = ref.offsetParent.clientWidth - ref.offsetLeft - ref.children[0].offsetWidth;

        if (offsetLeft < 0) {
            ref.style.marginLeft = '';
            ref.children[0].style.width = `${ref.children[0].offsetWidth}px`;
            ref.children[0].style.right = '100%'
        }
    }

    transformValue(value: number, decimalPlaces: number): number {
        // const num: number = 10 ** decimalPlaces;
        const num: number = 10 ** 7;
        return Math.round(value * num) / num;
    }
}

export function transformUnit(unit: string) {
    return ({
        euro: '€',
        Euro: '€',
        kw: 'kW',
        kwh: 'kWh',
        'kWh/Liter': 'kWh/l',
        liter: 'l',
        Liter: 'l',
        m2: 'm²',
        m3: 'm³',
        rm: 'Rm',
        srm: 'Srm',
        't CO₂ Äq/Euro': 'tCO₂Äq/€',
        't CO₂ Äq/Liter': 'tCO₂Äq/l',
        mw: 'MW',
        mwh: 'MWh'
    } as any)[unit] || unit;
}